export interface RuntimeCapabilities {
  workspace_admin: boolean;
}

export type RuntimeCapabilityStatus = 'idle' | 'loading' | 'ready';

export interface RuntimeCapabilitySnapshot {
  status: RuntimeCapabilityStatus;
  capabilities: RuntimeCapabilities;
}

const OFF_CAPABILITIES: RuntimeCapabilities = { workspace_admin: false };

let status: RuntimeCapabilityStatus = 'idle';
let capabilities: RuntimeCapabilities = { ...OFF_CAPABILITIES };
let inFlight: Promise<RuntimeCapabilities> | null = null;
const listeners = new Set<(snapshot: RuntimeCapabilitySnapshot) => void>();

function snapshot(): RuntimeCapabilitySnapshot {
  return {
    status,
    capabilities: { ...capabilities },
  };
}

function notify() {
  const current = snapshot();
  for (const listener of listeners) listener(current);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Parse the deliberately small public config response. Only a literal
 * boolean true enables a capability; every other shape remains fail-closed.
 */
export function parseRuntimeCapabilities(value: unknown): RuntimeCapabilities {
  const features = isRecord(value) ? value.features : undefined;
  return {
    workspace_admin: isRecord(features) && features.workspace_admin === true,
  };
}

export function getRuntimeCapabilitySnapshot(): RuntimeCapabilitySnapshot {
  return snapshot();
}

export function subscribeRuntimeCapabilities(
  listener: (snapshot: RuntimeCapabilitySnapshot) => void,
): () => void {
  listeners.add(listener);
  listener(snapshot());
  return () => listeners.delete(listener);
}

/**
 * Load deployment capabilities once per page lifetime. The response is
 * intentionally not persisted: a reload after an env/redeploy change is the
 * recovery boundary. Concurrent consumers share this request.
 */
export function loadRuntimeCapabilities(): Promise<RuntimeCapabilities> {
  if (inFlight) return inFlight;
  if (status === 'ready') return Promise.resolve({ ...capabilities });

  status = 'loading';
  notify();
  inFlight = fetch('/api/config', {
    credentials: 'include',
    headers: { 'X-Zenmetry-Client': 'web' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`config request failed: ${response.status}`);
      return response.json();
    })
    .then((value) => parseRuntimeCapabilities(value))
    .catch(() => ({ ...OFF_CAPABILITIES }))
    .then((next) => {
      capabilities = next;
      status = 'ready';
      notify();
      return { ...capabilities };
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/** Reset only for isolated tests; production callers rely on page reload. */
export function resetRuntimeCapabilitiesForTests(): void {
  inFlight = null;
  status = 'idle';
  capabilities = { ...OFF_CAPABILITIES };
  notify();
}
